/**
 * Regions: terrain, collision, and everything placed on it.
 *
 * A region is authored as data and consumed identically by the server (which builds
 * a collision map from it) and the client (which builds a mesh from it). Heights are
 * stored per tile corner rather than per tile so the ground interpolates smoothly
 * instead of looking like stacked boxes.
 *
 * Height never affects gameplay. It is the visual surface an entity is drawn on, and
 * two tiles at wildly different heights are still adjacent as far as combat and
 * pathfinding are concerned. Anything that should block movement is collision data.
 */

import type { DayPhase } from './time';

import { CollisionFlag, CollisionMap } from './collision';
import { MAX_PLANE } from './constants';
import { MAX_STEP_UP, tooSteep } from './scale';
import { isWading } from './shoreline';
import type { TilePos } from './tiles';

/**
 * Objects and spawns are stored in region-local tiles, so a region can be moved
 * without rewriting everything inside it. Only `spawnPoint` is in world tiles,
 * because it is compared against player positions directly.
 */
export interface RegionObject {
  /** Object definition id, resolved through the content registry. */
  id: number;
  tx: number;
  tz: number;
  plane: number;
  /** Quarter turns clockwise. */
  rotation: number;
  /**
   * How big it is drawn, where 1 is the size it was modelled at.
   *
   * Drawing only. What a thing blocks is decided by its definition and the tile
   * it stands on, so a tree scaled to twice the size does not quietly take twice
   * the ground.
   */
  scale?: number;
  /**
   * The height it is drawn at, instead of the ground or water under it.
   *
   * Boards laid out over water settle onto the surface and rise and fall with
   * it, which is right for a raft and wrong for a dock. A dock is built to the
   * height of its first piece, and every piece after it is told that height.
   */
  lift?: number;
  /**
   * A shape of its own, borrowed from something else.
   *
   * One kind of tree standing in a wood of pines and oaks. What it is for
   * belongs to the kind, and what it looks like belongs to the one standing
   * here, which is the difference between a wood and a row of the same tree.
   */
  looks?: number;
  /**
   * What this one can be worked for, when it differs from its kind.
   *
   * One stretch of water is netted, the next is fished with a rod, and a third
   * is netted for something else entirely. They are all the same piece of
   * scenery, so what a particular one offers belongs to the one standing there
   * rather than to the kind: without this, a second combination means a second
   * kind of fishing spot, and a coastline needs a dozen of them.
   *
   * Absent means whatever its kind offers, which is what everything placed
   * before this meant.
   */
  ways?: RegionWay[];
  /**
   * What this one does when it is pressed, when it differs from its kind.
   *
   * The same argument as the ways above, one step further on. A lever and the
   * gate it opens are a pair, and the act that joins them names the gate's
   * tile: written on the kind, every lever in the world opens the same gate,
   * and a second puzzle means a second kind of lever. Written here, one Lever
   * is placed wherever a lever is wanted and each one is told what it is for.
   *
   * Kept as written rather than typed, because an act is content and this file
   * is the shape of the ground: the server reads them through the same schema
   * that reads the ones on a kind, and anything it cannot read is left alone
   * rather than thrown away.
   *
   * Absent means whatever its kind does, which is what everything placed before
   * this meant.
   */
  acts?: unknown[];
}

/** One way of working one placed thing, and what comes up when it is worked. */
export interface RegionWay {
  /** The menu entry, matching a way its kind knows how to work. */
  action: string;
  /**
   * Everything that can come up here, weighted against each other.
   *
   * Empty falls back to what its kind gives, so a spot that is only turning
   * one way of fishing on and off does not have to restate the fish.
   *
   * What it takes and what it gives is not here, and deliberately: that
   * belongs to the thing caught. The same log is the same log wherever it is
   * cut, so one tree is a pine and the next an oak by what they hold rather
   * than by two copies of a level nobody can keep in step.
   */
  catches: { itemId: number; weight: number }[];
  /**
   * Extra experience for working this one, as a percentage on top.
   *
   * What a thing is worth belongs to the thing, and this is not that: it is
   * what this particular spot is worth over and above it, for a seam down a
   * mine nobody wants to walk to. Nought, or left out, is worth what its kind
   * is worth.
   */
  bonus?: number;
}

/** A rectangle of tiles, region-local like everything else placed in a region. */
export interface TileArea {
  tx: number;
  tz: number;
  width: number;
  height: number;
}

/**
 * Somewhere a kind of character keeps coming back.
 *
 * A zone is a set of tiles rather than a rectangle, because a clearing is not a
 * rectangle and neither is the inside of a building. It holds up to a number of
 * them at once and puts another one out on its own schedule.
 */
export interface RegionZone {
  id: number;
  npcId: number;
  /** What to call it. Empty falls back to whatever comes back in it. */
  name?: string;
  /** Region local tiles, packed as tz * width + tx. */
  tiles: number[];
  /** Most of them alive in the zone at once. */
  max: number;
  /** Ticks between one being put out, drawn between the two. */
  minTicks: number;
  maxTicks: number;
  /** Off means it keeps its shape and settings but puts nothing out. */
  paused?: boolean;
  /**
   * A flag the world holds that has to say something before it spawns.
   *
   * Against the world rather than against a player, because what stands in a
   * field is the same for everybody looking at it.
   */
  needsWorld?: string;
  worldIs?: string;
  /** The stretch of day it puts anything out in. Left out means any. */
  atTime?: DayPhase;
}

export interface RegionSpawn {
  npcId: number;
  tx: number;
  tz: number;
  plane: number;
  /**
   * Ticks before this one comes back after being killed. Left out, the character's
   * own timer is used, which is what nearly every spawn wants.
   */
  respawnTicks?: number;
  /**
   * Where this one is allowed to wander. Left out, it wanders its own range around
   * where it stands. Drawn in the editor rather than typed as a radius, because a
   * character that should stay behind a counter is a shape, not a number.
   */
  roam?: TileArea;
  /**
   * Where it may wander, drawn tile by tile as indices into the region.
   *
   * Present when somebody painted the ground rather than dragging a rectangle,
   * and what the character is held to when it is: a clearing has a shape and a
   * rectangle around it takes in half the wood.
   */
  roamTiles?: number[];
  /**
   * Other places this one stands, and what has to be true for it to.
   *
   * The tile it was placed on is where it stands when none of these apply. A
   * spot whose conditions a player meets takes over for that player alone, so
   * the same character can be in two places at once as far as two people are
   * concerned.
   */
  spots?: RegionSpot[];
}

/** One of the other places a character stands, and when. */
export interface RegionSpot {
  name?: string;
  tx: number;
  tz: number;
  /** The same conditions a line of conversation carries. */
  when: unknown[];
  /** Whether it walks there rather than being moved behind a blink of black. */
  walk?: boolean;
  /** Where it may wander once it is there. */
  roam?: { tx: number; tz: number; width: number; height: number };
}

/** Which way the water runs on one tile, and how fast. */
export interface RegionFlow {
  /** The tile it belongs to, as an index into the region's own grid. */
  at: number;
  /** Tiles a second along each axis. Nought in both is still water. */
  x: number;
  z: number;
}

export interface RegionDefinition {
  id: string;
  name: string;
  /** Tile coordinate of the region's south-west corner in world space. */
  originX: number;
  originZ: number;
  width: number;
  height: number;
  /**
   * Corner heights, ((width + 1) * (height + 1)) entries, row major from the
   * south-west corner. In tiles, so 1.0 is one tile of elevation.
   */
  heights: number[];
  /** Collision flags per tile, (width * height) entries, ground plane only for now. */
  flags: number[];
  /**
   * What each tile is made of, (width * height) entries.
   *
   * Nothing means the ground is coloured by where it sits: sand near the water,
   * grass above it, rock high up. A value overrides that, which is what turns a
   * worn path or a bare yard into something somebody chose rather than something
   * the height happened to produce.
   */
  ground?: number[];
  /** Rock overhead rather than sky: no sea reaches it and no sun lights it. */
  underground?: boolean;
  /**
   * Which layer of the world it sits on. Nought is the overworld.
   *
   * Two regions may cover the same ground on different layers, which is how a
   * dungeon comes to be under a town rather than beside it.
   */
  layer?: number;
  /**
   * The height of the water surface on each tile, (width * height) entries.
   *
   * Only meaningful where the tile is flagged as water. Water is not one sheet at
   * one level any more: a pond can sit in a hollow near the top of a hill and the
   * sea can be a hundred tiles below it, so where the surface of a piece of water
   * lies belongs to the tile rather than to the world.
   */
  water?: number[];
  /**
   * Which named area each tile belongs to, (width * height) entries.
   *
   * An index one past the start of `areas`, so nought means the tile is in none
   * of them. A border that follows a river, a ridge or the edge of a wood is not
   * a rectangle, so the shape of a place is kept as the tiles that say they are
   * in it, and asking where somebody is standing costs one lookup.
   */
  area?: number[];
  /**
   * Which way the water runs, on the few tiles where it runs at all.
   *
   * Kept as a list rather than as an array over every tile, because almost no
   * tile has one: the sea lies still, every lake lies still, and a continent of
   * two and a half million tiles has a few thousand of river. `at` is the tile
   * index, and `x` and `z` are how far the surface travels in a second, in
   * tiles along each axis, so the pair is a direction and a speed in one.
   *
   * Without it the water has to be guessed at from the shape of a body, which
   * gives one direction to a river that winds for a thousand tiles and gets the
   * sign of it right about half the time.
   */
  flow?: RegionFlow[];
  /**
   * The named parts of this place, in the order `area` refers to them.
   *
   * Ordered so that index one is the first area: a tile holding nought is in
   * none of them.
   */
  areas?: RegionArea[];
  objects: RegionObject[];
  /**
   * Loot lying about, put there on purpose.
   *
   * Furniture rather than a thing in transit: it does not expire, it comes
   * back after however long it is given, and it can belong to each person
   * separately.
   */
  loot?: {
    tx: number;
    tz: number;
    plane: number;
    itemId: number;
    quantity: number;
    /** How high off the ground it lies, for one left on a table or a shelf. */
    lift?: number;
    /** Ticks before it is there again. Nought never comes back. */
    emptyFor: number;
    /** Whether taking it is about one person or about the world. */
    each: boolean;
  }[];

  spawns: RegionSpawn[];
  /** Areas that keep a kind of character in them. */
  zones?: RegionZone[];
  /** Places that do something to whoever walks into them. */
  triggers?: RegionTrigger[];
  /** Where a new character starts, and where death returns them. */
  spawnPoint: TilePos;
}

/**
 * A named part of the world, with its place in the hierarchy above it.
 *
 * The continent is three regions across, each region has a north and a south
 * part, and each part holds areas and towns. The area is what somebody is told
 * they are standing in; the rest is for saying a name in full, as in South
 * Kestral, The Glass Waste.
 */
export interface RegionArea {
  name: string;
  /** A line about it, for whoever reads it off the map. */
  about?: string;
  /** The third of the continent it lies in, and which half of that third. */
  region?: string;
  part?: string;
  /** Somewhere people live, rather than open country. */
  town?: boolean;
}

/**
 * A place that does something when somebody walks into it.
 *
 * The conditions and effects are the same ones a conversation carries, which is
 * deliberate: a quest that starts when somebody reaches the far shore should be
 * written the same way as one that starts when somebody is told about it. The
 * shapes are kept loose here because the world does not read them, it hands
 * them to the same code that runs a line of dialogue.
 */
export interface RegionTrigger {
  id: number;
  name?: string;
  /** A line about the place, for whoever reads it off the map. */
  about?: string;
  tx: number;
  tz: number;
  width: number;
  height: number;
  plane: number;
  when: unknown[];
  does: unknown[];
  /** True for something that happens to a person once and never again. */
  once: boolean;
  /**
   * Whether this one names the ground it covers.
   *
   * A town, a district, a wood. Nothing has to happen when somebody walks in;
   * the ground simply has a name, and that name goes on the map in the corner
   * and into anything that says where to go.
   */
  names?: boolean;
}

/** Bilinear sample of the terrain surface. Used for placing meshes and camera focus. */
export function heightAt(region: RegionDefinition, x: number, z: number): number {
  const localX = x - region.originX;
  const localZ = z - region.originZ;

  const stride = region.width + 1;
  const x0 = Math.min(Math.max(Math.floor(localX), 0), region.width);
  const z0 = Math.min(Math.max(Math.floor(localZ), 0), region.height);
  const x1 = Math.min(x0 + 1, region.width);
  const z1 = Math.min(z0 + 1, region.height);

  const fx = Math.min(Math.max(localX - x0, 0), 1);
  const fz = Math.min(Math.max(localZ - z0, 0), 1);

  const h00 = region.heights[z0 * stride + x0] ?? 0;
  const h10 = region.heights[z0 * stride + x1] ?? 0;
  const h01 = region.heights[z1 * stride + x0] ?? 0;
  const h11 = region.heights[z1 * stride + x1] ?? 0;

  const top = h00 + (h10 - h00) * fx;
  const bottom = h01 + (h11 - h01) * fx;
  return top + (bottom - top) * fz;
}

/**
 * Builds the collision map the simulation runs against.
 *
 * Authored flags come first, then every solid object stamps its footprint on top.
 * Objects are the reason this is derived rather than authored directly: moving a
 * tree in the editor should not mean remembering to move its collision too.
 */
export type BarrierKind = 'solid' | 'wall' | 'open';

/**
 * Whether a piece was placed across the tile rather than along one of its sides.
 *
 * Four turns put a wall on each of a tile's four edges, which cannot make a
 * fence that runs at an angle: a diagonal run had to be built as a staircase of
 * north and east panels, and it looked like one. Four more turns put it corner
 * to corner instead, so the eight together are every eighth of a circle.
 *
 * Anything placed this way blocks the whole tile. A tile with a fence across
 * its middle has no half worth standing in, and half a tile is not a thing this
 * world has: the collision map is a tile and its four sides.
 */
export function isDiagonal(rotation: number): boolean {
  return (rotation & 4) !== 0;
}

/** Every position a wall can be placed in, edges first and then the diagonals. */
export const WALL_TURNS = 8;

/**
 * The eight positions in the order somebody turning a wall wants to meet them.
 *
 * The numbers themselves are edges first and diagonals after, because that is
 * what keeps every wall already standing where it stands. Turning through them
 * in that order would swing a fence a quarter of a circle four times and then
 * jump back, so the order to turn in is written down separately.
 */
const TURN_ORDER = [0, 4, 1, 5, 2, 6, 3, 7];

/**
 * The position a quarter turn round from this one, keeping which kind it is.
 *
 * Turning a whole selection turns everything in it by the same quarter, and a
 * fence lying across a tile stays lying across a tile: it moves to the next
 * corner pair rather than dropping back onto a side. So the quarters run in the
 * low two bits and the bit that says which kind it is comes along untouched.
 */
export function turnedQuarters(rotation: number, turns: number): number {
  return (((rotation + turns) & 3) | (rotation & 4)) & 7;
}

/**
 * The position one step round from this one.
 *
 * A step is an eighth of a circle for anything that can stand corner to corner
 * and a quarter for everything else: a chest at forty five degrees to the tile
 * it sits on looks dropped rather than placed.
 */
export function turnedBy(rotation: number, way: number, eighths: boolean): number {
  if (!eighths) return (rotation + way + 4) & 3;

  const at = TURN_ORDER.indexOf(rotation & 7);
  return TURN_ORDER[((((at < 0 ? 0 : at) + way) % 8) + 8) % 8] ?? 0;
}

/**
 * Which edge of a tile a wall stands on, from the quarter turns it was placed
 * with.
 *
 * Nought is the south edge and the turns run anticlockwise from there. The
 * edges themselves have not moved: the words for them changed when north
 * stopped meaning positive z, and every wall already standing keeps blocking
 * exactly what it blocked.
 */
export function wallFlagFor(rotation: number): number {
  switch (rotation & 3) {
    case 0:
      return CollisionFlag.WallSouth;
    case 1:
      return CollisionFlag.WallEast;
    case 2:
      return CollisionFlag.WallNorth;
    default:
      return CollisionFlag.WallWest;
  }
}

/** What one object adds to the tile it stands on, given how it was turned. */
export function barrierFlags(barrier: BarrierKind, rotation: number): number {
  if (barrier === 'wall')
    return isDiagonal(rotation) ? CollisionFlag.Blocked : wallFlagFor(rotation);
  return barrier === 'solid' ? CollisionFlag.Blocked : CollisionFlag.None;
}

export interface ObjectFootprint {
  width: number;
  height: number;
  solid: boolean;
  barrier?: BarrierKind;
  /** Something laid down and stood on, which is what makes an upper floor walkable. */
  floor?: boolean;
}

/** What an object blocks, following `solid` when nothing more specific was said. */
export function barrierOf(definition: ObjectFootprint): BarrierKind {
  return definition.barrier ?? (definition.solid ? 'solid' : 'open');
}

/**
 * Which of a tile's layers a piece of scenery belongs to.
 *
 * A tile is not one slot. Something is laid on it, something stands on it,
 * something covers it, and each of its four sides can carry a wall. Putting a
 * chest down on floorboards should no more take the boards away than it takes
 * the ground away, and the only way to know that is to know which layer each of
 * them is.
 */
export type ObjectLayer = 'floor' | 'standing' | 'roof' | 'wall';

export function layerOf(
  definition: ObjectFootprint & { floor?: boolean; roof?: boolean; layer?: ObjectLayer },
): ObjectLayer {
  if (definition.layer !== undefined) return definition.layer;
  if (definition.floor === true) return 'floor';
  if (definition.roof === true) return 'roof';
  return barrierOf(definition) === 'wall' ? 'wall' : 'standing';
}

export function buildCollisionMap(
  region: RegionDefinition,
  objectSize: (id: number) => ObjectFootprint | undefined,
): CollisionMap {
  const map = new CollisionMap(region.width, region.height, region.originX, region.originZ);

  const stride = region.width + 1;
  const cornerAt = (cx: number, cz: number): number => region.heights[cz * stride + cx] ?? 0;

  for (let tz = 0; tz < region.height; tz++) {
    for (let tx = 0; tx < region.width; tx++) {
      const flags = region.flags[tz * region.width + tx] ?? 0;

      // Ground steeper than a character can climb is not ground. Judged here,
      // once, off the corners themselves rather than off a flag somebody
      // remembered to set: a cliff is a cliff whether or not it was authored as
      // one, and the tools move corners far more often than they move flags.
      const steep = tooSteep([
        cornerAt(tx, tz),
        cornerAt(tx + 1, tz),
        cornerAt(tx, tz + 1),
        cornerAt(tx + 1, tz + 1),
      ]);

      /*
       * And the shallows, where there are any.
       *
       * The waterline is drawn where the ground comes up through the surface,
       * and that line crosses tiles rather than stopping at their edges, so a
       * tile the rules call dry can still be under water. Ground is shaped so
       * this cannot happen by more than a paddle: see `standOutOfWater`, which
       * the generators and the tools both run. This is the same rule read from
       * the other end, and on shaped ground it never fires.
       *
       * It is here for ground nobody has shaped yet. Painting water in the
       * tools puts it down before anything lifts the bank, and until something
       * does, the tile it half covers is not somewhere to stand.
       */
      let wet = flags;
      if ((flags & CollisionFlag.Water) === 0) {
        let surface = -Infinity;

        for (const [dx, dz] of [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ] as const) {
          const nx = tx + dx;
          const nz = tz + dz;
          if (nx < 0 || nz < 0 || nx >= region.width || nz >= region.height) continue;
          if (((region.flags[nz * region.width + nx] ?? 0) & CollisionFlag.Water) === 0) continue;

          const over = region.water?.[nz * region.width + nx] ?? 0;
          if (over > surface) surface = over;
        }

        if (
          surface > -Infinity &&
          isWading(
            [
              cornerAt(tx, tz),
              cornerAt(tx + 1, tz),
              cornerAt(tx, tz + 1),
              cornerAt(tx + 1, tz + 1),
            ],
            surface,
          )
        ) {
          wet = flags | CollisionFlag.Water;
        }
      }

      const held = steep ? wet | CollisionFlag.Blocked : wet;
      if (held !== 0) map.setFlags(region.originX + tx, region.originZ + tz, 0, held);
    }
  }

  // Above the ground there is nothing to stand on until somebody builds it, which
  // is the opposite of the ground itself. Everything up there starts blocked and
  // floors cut holes in it, so walking off the end of a landing is not possible
  // and a building with no upper storey has no upper storey to walk on.
  for (let plane = 1; plane <= MAX_PLANE; plane++) {
    for (let tz = 0; tz < region.height; tz++) {
      for (let tx = 0; tx < region.width; tx++) {
        map.setFlags(region.originX + tx, region.originZ + tz, plane, CollisionFlag.Blocked);
      }
    }
  }

  // Boards laid over water at ground level: a dock or a bridge. The water is
  // still there and still drawn, but somebody can walk across it, and boards are
  // the only thing that makes water crossable at all. Held back until every
  // other object has been applied, because a solid thing sharing the tile has
  // the final say and may be listed after the boards.
  const decked: { tx: number; tz: number }[] = [];
  const solid = new Set<number>();
  const key = (tx: number, tz: number): number => tz * region.width + tx;

  for (const object of region.objects) {
    const definition = objectSize(object.id);
    if (!definition) continue;

    if (definition.floor && object.plane === 0) {
      const spread = object.rotation % 2 === 1;
      const across = spread ? definition.height : definition.width;
      const along = spread ? definition.width : definition.height;

      for (let dz = 0; dz < along; dz++) {
        for (let dx = 0; dx < across; dx++) {
          decked.push({ tx: object.tx + dx, tz: object.tz + dz });
        }
      }
      continue;
    }

    // A floor is the one thing that makes room rather than taking it.
    if (definition.floor && object.plane > 0) {
      const spread = object.rotation % 2 === 1;
      const across = spread ? definition.height : definition.width;
      const along = spread ? definition.width : definition.height;

      for (let dz = 0; dz < along; dz++) {
        for (let dx = 0; dx < across; dx++) {
          map.clearFlags(
            region.originX + object.tx + dx,
            region.originZ + object.tz + dz,
            object.plane,
            CollisionFlag.Blocked,
          );
        }
      }
      continue;
    }

    const barrier = barrierOf(definition);
    if (barrier === 'open') continue;

    // A wall stands on one edge of one tile, so it never has a footprint to walk.
    if (barrier === 'wall') {
      map.addFlags(
        region.originX + object.tx,
        region.originZ + object.tz,
        object.plane,
        wallFlagFor(object.rotation),
      );
      continue;
    }

    // A quarter or three-quarter turn swaps the footprint's axes.
    const turned = object.rotation % 2 === 1;
    const width = turned ? definition.height : definition.width;
    const height = turned ? definition.width : definition.height;

    for (let dz = 0; dz < height; dz++) {
      for (let dx = 0; dx < width; dx++) {
        map.addFlags(
          region.originX + object.tx + dx,
          region.originZ + object.tz + dz,
          object.plane,
          CollisionFlag.Blocked,
        );

        if (object.plane === 0) solid.add(key(object.tx + dx, object.tz + dz));
      }
    }
  }

  // The boards, last. Only over water, and only where nothing solid is standing
  // on the same tile: a dock is a way across the water, not a way through a
  // wall somebody built on it.
  const laid: { tx: number; tz: number }[] = [];
  for (const board of decked) {
    if (solid.has(key(board.tx, board.tz))) continue;

    const tx = region.originX + board.tx;
    const tz = region.originZ + board.tz;
    if ((map.flagsAt(tx, tz, 0) & CollisionFlag.Water) === 0) continue;

    map.clearFlags(tx, tz, 0, CollisionFlag.Blocked);
    map.addFlags(tx, tz, 0, CollisionFlag.Decked);
    laid.push({ tx, tz });
  }

  // And the edges of them. Boards laid over water stand at their own height,
  // so where a deck meets the land the surface jumps rather than slopes, and a
  // jump is a step rather than a walk. Anything higher than a step is closed
  // off, which is what sends somebody round to the end of the dock where it
  // actually meets the ground instead of up the side of it.
  for (const { tx, tz } of laid) {
    const deck = surfaceOf(region, tx, tz);

    for (const [dx, dz, from, to] of [
      [0, -1, CollisionFlag.WallNorth, CollisionFlag.WallSouth],
      [1, 0, CollisionFlag.WallEast, CollisionFlag.WallWest],
      [0, 1, CollisionFlag.WallSouth, CollisionFlag.WallNorth],
      [-1, 0, CollisionFlag.WallWest, CollisionFlag.WallEast],
    ] as const) {
      const overX = tx + dx;
      const overZ = tz + dz;
      if (!map.inBounds(overX, overZ)) continue;

      const beside = map.flagsAt(overX, overZ, 0);
      // Another decked tile is the same dock, and anything already closed needs
      // nothing added to it.
      if ((beside & CollisionFlag.Decked) !== 0) continue;
      if ((beside & CollisionFlag.Blocked) !== 0) continue;

      if (Math.abs(deck - surfaceOf(region, overX, overZ)) <= MAX_STEP_UP) continue;

      map.addFlags(tx, tz, 0, from);
      map.addFlags(overX, overZ, 0, to);
    }
  }

  return map;
}

/**
 * What somebody standing on a tile is standing on.
 *
 * The ground itself, or the water surface where boards have been laid over it:
 * a dock is walked on at the height of the water it crosses, not at the height
 * of the seabed under it. Averaged over the tile's corners, because that is
 * where the middle of the tile is and the middle is where somebody stands.
 */
export function surfaceOf(region: RegionDefinition, tx: number, tz: number): number {
  const localX = tx - region.originX;
  const localZ = tz - region.originZ;
  const stride = region.width + 1;

  const water = region.water?.[localZ * region.width + localX] ?? 0;
  if (water !== 0) return water;

  return (
    ((region.heights[localZ * stride + localX] ?? 0) +
      (region.heights[localZ * stride + localX + 1] ?? 0) +
      (region.heights[(localZ + 1) * stride + localX] ?? 0) +
      (region.heights[(localZ + 1) * stride + localX + 1] ?? 0)) /
    4
  );
}

/** Converts a world tile to a region-local tile. */
export function toLocal(region: RegionDefinition, tile: TilePos): TilePos {
  return { tx: tile.tx - region.originX, tz: tile.tz - region.originZ, plane: tile.plane };
}

export function toWorld(region: RegionDefinition, tile: TilePos): TilePos {
  return { tx: tile.tx + region.originX, tz: tile.tz + region.originZ, plane: tile.plane };
}

export function containsTile(region: RegionDefinition, tile: TilePos): boolean {
  const local = toLocal(region, tile);
  return local.tx >= 0 && local.tz >= 0 && local.tx < region.width && local.tz < region.height;
}

/**
 * What a tile can be made of.
 *
 * Nothing is the default: the ground is coloured by where it sits. The rest are
 * chosen, and the names are the ones anybody would use for them.
 */
export const GroundKind = {
  Auto: 0,
  Grass: 1,
  Dirt: 2,
  Sand: 3,
  Rock: 4,
  Path: 5,
  /** Snow, which the far north and every peak is under. */
  Snow: 6,
  /** Cold ash, which is what a volcano puts down around itself. */
  Ash: 7,
  /** Bare stone still warm, close in to a crater. */
  Lava: 8,
  /** Water worn pebbles, which is what a coast the weather reaches is made of. */
  Shingle: 9,
  /** Wet silt, at the mouth of a river and anywhere the water comes and goes. */
  Mud: 10,
} as const;

export type GroundKindValue = (typeof GroundKind)[keyof typeof GroundKind];

export const GROUND_NAMES: Readonly<Record<string, number>> = {
  auto: GroundKind.Auto,
  grass: GroundKind.Grass,
  dirt: GroundKind.Dirt,
  sand: GroundKind.Sand,
  rock: GroundKind.Rock,
  path: GroundKind.Path,
  snow: GroundKind.Snow,
  ash: GroundKind.Ash,
  lava: GroundKind.Lava,
  shingle: GroundKind.Shingle,
  mud: GroundKind.Mud,
};

/** Somewhere with a size, in the one space every region shares. */
export interface Placed {
  originX: number;
  originZ: number;
  width: number;
  height: number;
  /**
   * Which layer of the world it is on. Nought is the overworld.
   *
   * Left out means the overworld, so everything written before there were
   * layers is on the one everybody walks about on.
   */
  layer?: number;
}

/**
 * Whether two placed things cover any of the same ground.
 *
 * The world is one map, so two regions in the same place is not a layout
 * choice: it is a mistake that would put two floors on one tile. Kept here
 * rather than beside the region files because the tools ask it while somebody
 * is dragging an island about, and the world asks it before agreeing.
 */
export function placesOverlap(one: Placed, other: Placed): boolean {
  if (one.width <= 0 || one.height <= 0 || other.width <= 0 || other.height <= 0) return false;

  // Different layers never clash: a cellar under a house is the same ground and
  // a different place, which is the whole point of having layers at all.
  if ((one.layer ?? 0) !== (other.layer ?? 0)) return false;

  return (
    one.originX < other.originX + other.width &&
    other.originX < one.originX + one.width &&
    one.originZ < other.originZ + other.height &&
    other.originZ < one.originZ + one.height
  );
}

/** A region's place in the world, as much of it as a seam needs to know. */
export interface PlacedRegion extends Placed {
  id: string;
}

/**
 * The region waiting on the other side of an edge, and where to land in it.
 *
 * Walking off the end of one region and into the next is the whole of a world
 * being one map rather than a set of islands, and it comes down to arithmetic:
 * somebody standing on the last tile of a region, having walked outwards, is one
 * step from the first tile of whatever lies beyond. If something is placed
 * there, that step should happen there.
 *
 * Nothing at all where the walk was inwards, where the tile is not on an edge,
 * or where the ground beyond belongs to nobody. Diagonals are taken one axis at
 * a time, the axis that actually leaves the region, because a corner belongs to
 * two edges and a player only ever leaves by one of them.
 */
export function seamAt(
  region: PlacedRegion,
  plan: readonly PlacedRegion[],
  tile: { tx: number; tz: number },
  from: { tx: number; tz: number },
): { id: string; tx: number; tz: number } | null {
  const stepX = Math.sign(tile.tx - from.tx);
  const stepZ = Math.sign(tile.tz - from.tz);
  if (stepX === 0 && stepZ === 0) return null;

  const layer = region.layer ?? 0;

  const left = region.originX;
  const right = region.originX + region.width - 1;
  const top = region.originZ;
  const bottom = region.originZ + region.height - 1;

  // Only from the outermost row or column, and only outwards.
  const leavingX = (stepX < 0 && tile.tx === left) || (stepX > 0 && tile.tx === right);
  const leavingZ = (stepZ < 0 && tile.tz === top) || (stepZ > 0 && tile.tz === bottom);
  if (!leavingX && !leavingZ) return null;

  const beyond = {
    tx: tile.tx + (leavingX ? stepX : 0),
    tz: tile.tz + (leavingZ ? stepZ : 0),
  };

  const found = plan.find(
    (one) =>
      one.id !== region.id &&
      // Only along the layer being walked on: an edge is not a hole in the floor.
      (one.layer ?? 0) === layer &&
      one.width > 0 &&
      beyond.tx >= one.originX &&
      beyond.tx < one.originX + one.width &&
      beyond.tz >= one.originZ &&
      beyond.tz < one.originZ + one.height,
  );

  return found ? { id: found.id, tx: beyond.tx, tz: beyond.tz } : null;
}
